import type { JurisdictionRules } from '../types';
import type { AppliedOtMethod } from '../types/payroll-export';

export interface DailyClassification {
  readonly regularMinutes: number;
  readonly overtimeMinutes: number;
  readonly doubleOvertimeMinutes: number;
}

export function classifyDailyHours(
  totalPaidMinutes: number,
  rules: JurisdictionRules,
  isSeventhConsecutiveDay: boolean
): DailyClassification {
  if (totalPaidMinutes <= 0) {
    return { regularMinutes: 0, overtimeMinutes: 0, doubleOvertimeMinutes: 0 };
  }

  // 7th consecutive day rule (California)
  if (isSeventhConsecutiveDay && rules.seventhDayRule) {
    const otThreshold = 480; // 8 hours in minutes
    const otMinutes = Math.min(totalPaidMinutes, otThreshold);
    const dtMinutes = Math.max(0, totalPaidMinutes - otThreshold);
    return { regularMinutes: 0, overtimeMinutes: otMinutes, doubleOvertimeMinutes: dtMinutes };
  }

  const dailyOt = rules.dailyOtThreshold !== null ? rules.dailyOtThreshold * 60 : null;
  const dailyDt = rules.dailyDtThreshold !== null ? rules.dailyDtThreshold * 60 : null;

  // No daily thresholds (FLSA) — all minutes are regular (weekly OT handled separately)
  if (dailyOt === null) {
    return { regularMinutes: totalPaidMinutes, overtimeMinutes: 0, doubleOvertimeMinutes: 0 };
  }

  const regular = Math.min(totalPaidMinutes, dailyOt);
  const afterRegular = Math.max(0, totalPaidMinutes - dailyOt);

  let ot: number;
  let dt: number;
  if (dailyDt !== null) {
    const otCap = dailyDt - dailyOt;
    ot = Math.min(afterRegular, otCap);
    dt = Math.max(0, afterRegular - otCap);
  } else {
    ot = afterRegular;
    dt = 0;
  }

  return { regularMinutes: regular, overtimeMinutes: ot, doubleOvertimeMinutes: dt };
}

export interface WeeklyAccumulation {
  readonly totalRegularMinutes: number;
  readonly totalOtMinutes: number;
  readonly totalDtMinutes: number;
  readonly dailyOtTotal: number;
  readonly weeklyOtTotal: number;
  readonly appliedOtMethod: AppliedOtMethod;
  readonly seventhDayApplied: boolean;
  readonly consecutiveDays: number;
}

export function accumulateWeeklyOt(
  dailyClassifications: readonly DailyClassification[],
  rules: JurisdictionRules,
  daysWorked: readonly boolean[] // length 7, true if employee worked that day of the workweek
): WeeklyAccumulation {
  // Sum daily classifications
  const dailyRegular = dailyClassifications.reduce((s, d) => s + d.regularMinutes, 0);
  const dailyOt = dailyClassifications.reduce((s, d) => s + d.overtimeMinutes, 0);
  const dailyDt = dailyClassifications.reduce((s, d) => s + d.doubleOvertimeMinutes, 0);

  const totalWorked = dailyRegular + dailyOt + dailyDt;
  const weeklyThreshold = rules.weeklyOtThreshold * 60;

  // Calculate consecutive days worked (for 7th day rule tracking)
  let maxConsecutive = 0;
  let currentStreak = 0;
  for (const worked of daysWorked) {
    if (worked) {
      currentStreak += 1;
      maxConsecutive = Math.max(maxConsecutive, currentStreak);
    } else {
      currentStreak = 0;
    }
  }
  const seventhDayApplied = rules.seventhDayRule && maxConsecutive >= 7;

  // Daily OT total = all OT + DT from daily classification
  const dailyOtTotal = dailyOt + dailyDt;

  // Weekly OT total = max(0, total_worked - weekly_threshold)
  const weeklyOtTotal = Math.max(0, totalWorked - weeklyThreshold);

  // No-double-dip: pay whichever is greater
  if (dailyOtTotal >= weeklyOtTotal) {
    return {
      totalRegularMinutes: dailyRegular,
      totalOtMinutes: dailyOt,
      totalDtMinutes: dailyDt,
      dailyOtTotal,
      weeklyOtTotal,
      appliedOtMethod: 'daily',
      seventhDayApplied,
      consecutiveDays: maxConsecutive,
    };
  }

  // Weekly wins — reclassify the difference as additional weekly OT
  const additionalOt = weeklyOtTotal - dailyOtTotal;
  return {
    totalRegularMinutes: dailyRegular - additionalOt,
    totalOtMinutes: dailyOt + additionalOt,
    totalDtMinutes: dailyDt,
    dailyOtTotal,
    weeklyOtTotal,
    appliedOtMethod: 'weekly',
    seventhDayApplied,
    consecutiveDays: maxConsecutive,
  };
}
